/**
 * First-party cookie holding the referring employee's UUID so referral
 * attribution survives the member leaving and coming back later (the
 * form itself persists to per-tab sessionStorage, which is wrong for
 * attribution: a member who clicks an employee's link, closes the tab,
 * and returns tomorrow should still credit that employee).
 *
 * Semantics:
 *   - Last click wins: a newer `?ref=<id>` link overwrites the cookie.
 *   - Rolling 30-day expiry, refreshed on every visit while set.
 *   - Consumed on successful submission: one referral click credits at
 *     most one application. Starting another application later without
 *     a fresh link click is unattributed.
 */

const REFERRAL_COOKIE_NAME = 'loan-app-referral';
const REFERRAL_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;

/**
 * RFC 4122 UUID format (any version). Applied on both write and read:
 * the cookie is user-editable, so a read is only trusted when it still
 * looks like an employee UUID. The API re-validates the id against the
 * active-employee directory regardless — this check just keeps garbage
 * out of client state.
 */
export const REFERRAL_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function readReferralCookie(): string {
  if (typeof document === 'undefined') return '';
  const entry = document.cookie
    .split('; ')
    .find((part) => part.startsWith(`${REFERRAL_COOKIE_NAME}=`));
  if (!entry) return '';
  const value = decodeURIComponent(entry.slice(REFERRAL_COOKIE_NAME.length + 1));
  return REFERRAL_ID_PATTERN.test(value) ? value : '';
}

export function writeReferralCookie(employeeId: string): void {
  if (typeof document === 'undefined') return;
  if (!REFERRAL_ID_PATTERN.test(employeeId)) return;
  const secure = window.location.protocol === 'https:' ? '; secure' : '';
  document.cookie =
    `${REFERRAL_COOKIE_NAME}=${encodeURIComponent(employeeId)}` +
    `; max-age=${REFERRAL_COOKIE_MAX_AGE_SECONDS}; path=/; samesite=lax${secure}`;
}

export function clearReferralCookie(): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${REFERRAL_COOKIE_NAME}=; max-age=0; path=/; samesite=lax`;
}
